"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const bind_decorator_1 = __importDefault(require("bind-decorator"));
const debounce_1 = require("debounce");
const mobx_1 = require("mobx");
const ecommerce_1 = require("../../api/ecommerce");
const filter_1 = require("../helper/filter");
const Route_1 = __importDefault(require("./Route"));
const filterParamsRegex = /filter\[([a-z0-9]+)]/i;
class ProductFilter {
    debouncedApplyFilter = (0, debounce_1.debounce)(this.applyFilter, 250);
    storedFilter = {};
    // Used to add a little delay between the filter input and the list re-render. (For performance reasons)
    appliedFilter = this.storedFilter;
    get filter() {
        return this.storedFilter;
    }
    constructor() {
        (0, mobx_1.makeObservable)(this, {
            storedFilter: mobx_1.observable,
            appliedFilter: mobx_1.observable,
            filter: mobx_1.computed,
            setFilterValue: mobx_1.action.bound,
            reset: mobx_1.action.bound,
            applyFilter: mobx_1.action.bound,
            restoreFilter: mobx_1.action.bound,
            setFilterFromURLParams: mobx_1.action.bound,
        });
        (0, mobx_1.reaction)(() => Route_1.default.location?.search, (search) => {
            this.setFilterFromURLParams(new URLSearchParams(search));
        });
        (0, mobx_1.reaction)(() => this.appliedFilter, (filter) => {
            this.setFilterValuesToUrlParams(filter);
        });
    }
    setFilterValue(key, value, autoApply) {
        const filter = { ...this.storedFilter };
        if (value === undefined || value === null) {
            delete filter[key];
        }
        else {
            filter[key] = value;
        }
        this.storedFilter = filter;
        if (autoApply === true) {
            this.debouncedApplyFilter();
        }
    }
    reset() {
        this.storedFilter = this.appliedFilter = {};
    }
    filterProducts(category, products) {
        const filter = this.appliedFilter;
        const filterKeys = Object.keys(filter);
        const activeFilterKeys = filterKeys.filter((key) => {
            const value = filter[key];
            return value !== null && value !== undefined && value !== '';
        });
        if (activeFilterKeys.length === 0) {
            return products;
        }
        return products.filter((product) => activeFilterKeys.every((propertyNameOrId) => {
            const value = filter[propertyNameOrId];
            switch (propertyNameOrId) {
                case 'priceFrom':
                    return (0, filter_1.matchesPriceFrom)(product, Number(value));
                case 'priceTo':
                    return (0, filter_1.matchesPriceTo)(product, Number(value));
                case 'materialGroup':
                    return (0, filter_1.matchesMaterialGroup)(product, value.split(',').filter((item) => !!item));
                case 'material':
                    return (0, filter_1.matchesMaterial)(product, value.split(',').filter((item) => !!item));
                case 'tool':
                    return (0, filter_1.matchesTool)(product, value.split(',').filter((item) => !!item));
            }
            const filterProperty = category.extensions?.massiveCategory?.filterProperties?.find(({ propertyId }) => propertyId === propertyNameOrId);
            switch (filterProperty?.filterType) {
                case ecommerce_1.MassiveCategoryFilterPropertyFilterTypeEnum.SingleChoice:
                    return (0, filter_1.matchesPropertyWithSingleSelection)(product, value);
                case ecommerce_1.MassiveCategoryFilterPropertyFilterTypeEnum.MultiChoice:
                    return (0, filter_1.matchesPropertyWithMultiSelection)(product, value.split(',').filter((item) => !!item));
                case ecommerce_1.MassiveCategoryFilterPropertyFilterTypeEnum.Range:
                    return (0, filter_1.matchesPropertyWithRangeSelection)(product, products, propertyNameOrId, Number(value));
            }
        }));
    }
    applyFilter() {
        this.appliedFilter = { ...this.storedFilter };
    }
    restoreFilter() {
        this.storedFilter = { ...this.appliedFilter };
    }
    generateSearchString(filter) {
        const { search } = Route_1.default.location ?? {};
        const urlParams = new URLSearchParams(search);
        Array.from(urlParams.keys()).forEach((name) => {
            if (filterParamsRegex.test(name)) {
                urlParams.delete(name);
            }
        });
        Object.entries(filter).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return;
            }
            urlParams.set(`filter[${key}]`, `${value}`);
        });
        return `?${urlParams.toString()}`;
    }
    setFilterValuesToUrlParams(filter) {
        const { pathname, search } = Route_1.default.location ?? {};
        const newSearch = this.generateSearchString(filter);
        const normalizedSearch = (search ?? '').replace(/^\?/gi, '').trim();
        const normalizedNewSearch = newSearch.replace(/^\?/gi, '').trim();
        if (normalizedSearch !== normalizedNewSearch) {
            Route_1.default.history?.push(pathname + newSearch);
        }
    }
    setFilterFromURLParams(urlParams) {
        this.reset();
        Array.from(urlParams.keys()).forEach((name) => {
            if (filterParamsRegex.test(name)) {
                const match = filterParamsRegex.exec(name);
                if (!match) {
                    return;
                }
                const key = match[1];
                const value = urlParams.get(name);
                if (!value) {
                    return;
                }
                if (['priceFrom', 'priceTo'].indexOf(key) !== -1) {
                    const numberValue = Number(value);
                    if (!isNaN(numberValue)) {
                        this.setFilterValue(key, numberValue);
                    }
                }
                else {
                    this.setFilterValue(key, value);
                }
            }
        });
        this.applyFilter();
    }
}
__decorate([
    bind_decorator_1.default
], ProductFilter.prototype, "filterProducts", null);
__decorate([
    bind_decorator_1.default
], ProductFilter.prototype, "generateSearchString", null);
__decorate([
    bind_decorator_1.default
], ProductFilter.prototype, "setFilterValuesToUrlParams", null);
exports.default = new ProductFilter();
